import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

@Component({
  selector: 'app-final-form',
  templateUrl: './final-form.component.html',
  styleUrls: ['./final-form.component.scss']
})
export class FinalFormComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  databaseForm!: FormGroup;
  formSubmitted = false;
  fileUploaded = false;

  heatingLogEntryOptions: string[] = ['Yes', 'No'];
  cargoTypeOptions = ['Crude Oil', 'Fuel Oil', 'Veg Oil', 'VGO'];
  heatingMethodOptions = ['Heating Coil', 'Deck Heat Exchanger'];
  voyageStatusOptions = ['Running', 'Completed', 'Not Started'];

  constructor(
    private fb: FormBuilder,
    private commonService: CargoheatingCommonService,
    private toastr: ToastrService,
    private router: Router,
    private requestFormService: RequestFormService
  ) {
    this.databaseForm = this.createForm();
  }

  ngOnInit(): void {
    this.commonService.getFleetViewRowDat().subscribe(data => {
      const actualData = data?.data || data;
      if (actualData) this.populateForm(actualData);
    });
  }

  /** ------------------- FormArray Getters ------------------- **/
  get loadPortDetails(): FormArray { return this.databaseForm.get('loadPortDetails') as FormArray; }
  get dischargePortDetails(): FormArray { return this.databaseForm.get('dischargePortDetails') as FormArray; }
  get loadPorts(): FormArray { return this.databaseForm.get('loadPorts') as FormArray; }
  get dischargePorts(): FormArray { return this.databaseForm.get('dischargePorts') as FormArray; }

  /** ------------------- Form Creation ------------------- **/
  private createForm(): FormGroup {
    return this.fb.group({
      picAnalyst: ['', Validators.required],
      clientsName: ['', Validators.required],
      dateReceivedFromClient: ['', Validators.required],
      merakiUniqueNumber: [''],
      voyageRunningOrCompleted: [''],
      finalReportStatusSent: [''],
      reportSentDate: [''],
      vessel: ['', Validators.required],
      vesselIMO: ['', Validators.required],
      heatingCoilsOrDeckHeatExchanger: [''],
      voyageNumber: [''],
      cargoName: [''],
      cargoType: [''],
      heatingInstructions: [''],
      averageLoadedCargoTempDegC: [''],
      averageDischCargoTempDegC: [''],
      merakiTotalEstimatedHeatingConsumptionsMT: ['0'],
      totalHeatingBunkerConsumptionMT: [''],
      aeConsumptionForBoilerOperationMT: [''],
      deckHeatExchangerBunkerConsumptionMT: [''],
      totalBunkerConsumptionMT: [''],
      heatingLogAnalysisEntry: [''],

      loadPortDetails: this.fb.array([this.createLoadPortGroup()]),
      dischargePortDetails: this.fb.array([this.createDischargePortGroup()]),
      loadPorts: this.fb.array([this.createLoadPortEventGroup()]),
      dischargePorts: this.fb.array([this.createDischargePortEventGroup()])
    });
  }

  /** ------------------- FormGroup Creators ------------------- **/
  private createLoadPortGroup(): FormGroup {
    return this.fb.group({
      loadPort: [''],
      departure: ['']
    });
  }

  private createDischargePortGroup(): FormGroup {
    return this.fb.group({
      dischargePort: [''],
      arrival: ['']
    });
  }

  private createLoadPortEventGroup(): FormGroup {
    return this.fb.group({
      port_sequence: [1],
      port_name: [''],
      events: this.fb.group({
        eosp: [''],
        all_fast: [''],
        cosp: ['']
      })
    });
  }

  private createDischargePortEventGroup(): FormGroup {
    return this.fb.group({
      port_sequence: [1],
      port_name: [''],
      events: this.fb.group({
        eosp: [''],
        all_fast: [''],
        cosp: ['']
      })
    });
  }

  /** ------------------- Add / Remove Rows ------------------- **/
  addLoadPortRow() { this.loadPortDetails.push(this.createLoadPortGroup()); }
  removeLoadPortRow(index: number) { if (this.loadPortDetails.length > 1) this.loadPortDetails.removeAt(index); }

  addDischargePortRow() { this.dischargePortDetails.push(this.createDischargePortGroup()); }
  removeDischargePortRow(index: number) { if (this.dischargePortDetails.length > 1) this.dischargePortDetails.removeAt(index); }

  addLoadPortEventRow() { this.loadPorts.push(this.createLoadPortEventGroup()); }
  removeLoadPortEventRow(index: number) { if (this.loadPorts.length > 1) this.loadPorts.removeAt(index); }

  addDischargePortEventRow() { this.dischargePorts.push(this.createDischargePortEventGroup()); }
  removeDischargePortEventRow(index: number) { if (this.dischargePorts.length > 1) this.dischargePorts.removeAt(index); }

  /** ------------------- Populate Form ------------------- **/
  populateForm(data: any): void {
    if (!data) return;

    this.databaseForm.patchValue({
      picAnalyst: data.picAnalyst || '',
      clientsName: data.clientsName || '',
      dateReceivedFromClient: data.dateReceivedFromClient || '',
      merakiUniqueNumber: data.merakiUniqueNumber || '',
      voyageRunningOrCompleted: data.voyageRunningOrCompleted || '',
      finalReportStatusSent: data.finalReportStatusSent || '',
      reportSentDate: data.reportSentDate || '',
      vessel: data.vessel || '',
      vesselIMO: data.vesselIMO || '',
      heatingCoilsOrDeckHeatExchanger: data.heatingCoilsOrDeckHeatExchanger || '',
      voyageNumber: data.voyageNumber || '',
      cargoName: data.cargoName || '',
      cargoType: data.cargoType || '',
      heatingInstructions: data.heatingInstructions || '',
      averageLoadedCargoTempDegC: data.averageLoadedCargoTempDegC || '',
      averageDischCargoTempDegC: data.averageDischCargoTempDegC || '',
      merakiTotalEstimatedHeatingConsumptionsMT: data.merakiTotalEstimatedHeatingConsumptionsMT || '0',
      totalHeatingBunkerConsumptionMT: data.totalHeatingBunkerConsumptionMT || '',
      aeConsumptionForBoilerOperationMT: data.aeConsumptionForBoilerOperationMT || '',
      deckHeatExchangerBunkerConsumptionMT: data.deckHeatExchangerBunkerConsumptionMT || '',
      totalBunkerConsumptionMT: data.totalBunkerConsumptionMT || '',
      heatingLogAnalysisEntry: data.heatingLogAnalysisEntry || ''
    });

    // Load Port Details
    this.loadPortDetails.clear();
    (data.loadPortDetails ? JSON.parse(data.loadPortDetails) : []).forEach((item: any) => {
      this.loadPortDetails.push(this.fb.group({
        loadPort: [item.loadPort1 || ''],
        departure: [item.departure1 || '']
      }));
    });

    this.dischargePortDetails.clear();
    (data.dischargePortDetails ? JSON.parse(data.dischargePortDetails) : []).forEach((item: any) => {
      this.dischargePortDetails.push(this.fb.group({
        dischargePort: [item.dischargePort1 || ''],
        arrival: [item.arrival1 || '']
      }));
    });

    this.loadPorts.clear();
    (data.load_ports || []).forEach((port: any) => {
      this.loadPorts.push(this.fb.group({
        port_sequence: [port.port_sequence],
        port_name: [port.port_name],
        events: this.fb.group({
          eosp: [port.events.eosp || ''],
          all_fast: [port.events.all_fast || ''],
          cosp: [port.events.cosp || '']
        })
      }));
    });

    this.dischargePorts.clear();
    (data.discharge_ports || []).forEach((port: any) => {
      this.dischargePorts.push(this.fb.group({
        port_sequence: [port.port_sequence],
        port_name: [port.port_name],
        events: this.fb.group({
          eosp: [port.events.eosp || ''],
          all_fast: [port.events.all_fast || ''],
          cosp: [port.events.cosp || '']
        })
      }));
    });
  }

  /** ------------------- File Upload ------------------- **/
  uploadFile(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      console.log('Excel file uploaded:', file.name);
      this.fileUploaded = true;
    }
  }

  /** ------------------- Submit Form with File ------------------- **/
  createFinalReport(): void {
    if (!this.fileInput.nativeElement.files?.length) {
      this.toastr.error('Please select a file before submitting');
      return;
    }

    const payload = this.databaseForm.getRawValue();
    const formData = new FormData();

    Object.keys(payload).forEach(key => {
      if (['loadPortDetails', 'dischargePortDetails', 'loadPorts', 'dischargePorts'].includes(key)) return;
      formData.append(key, payload[key] ?? '');
    });

    formData.append('loadPortDetails', JSON.stringify(payload.loadPortDetails));
    formData.append('dischargePortDetails', JSON.stringify(payload.dischargePortDetails));
    formData.append('loadPorts', JSON.stringify(payload.loadPorts));
    formData.append('dischargePorts', JSON.stringify(payload.dischargePorts));

    formData.append('file', this.fileInput.nativeElement.files![0]);

    this.commonService.generateFinalReport(formData).subscribe({
      next: () => this.toastr.success('Final report generated successfully!'),
      error: (err) => this.toastr.error('Error generating final report')
    });
  }

  /** ------------------- Submit without file ------------------- **/
  submitClick(): void {
    this.formSubmitted = true;
    if (!this.databaseForm.valid) {
      this.toastr.info('Please fill all required fields');
      return;
    }

    const formData = this.databaseForm.getRawValue();
    const loadPortDetailsFormatted = formData.loadPortDetails.map((item: any, index: number) => ({
      [`loadPort${index + 1}`]: item.loadPort,
      [`departure${index + 1}`]: item.departure
    }));

    const dischargePortDetailsFormatted = formData.dischargePortDetails.map((item: any, index: number) => ({
      [`dischargePort${index + 1}`]: item.dischargePort,
      [`arrival${index + 1}`]: item.arrival
    }));

    const finalPayload = {
      ...formData,
      loadPortDetails: JSON.stringify(loadPortDetailsFormatted),
      dischargePortDetails: JSON.stringify(dischargePortDetailsFormatted)
    };

    this.commonService.postFormData(finalPayload).subscribe({
      next: () => this.toastr.success('Form submitted successfully'),
      error: () => this.toastr.error('Error submitting form')
    });
  }

  /** ------------------- Clear Form ------------------- **/
  clearClick(): void {
    this.formSubmitted = false;
    this.databaseForm.reset();
    this.commonService.clearFleetViewRowDat();
    this.fileInput.nativeElement.value = '';
    this.fileUploaded = false;
  }

  /** ------------------- Generate Unique Number ------------------- **/
  generateUniqueNumber() {
    const dateValue = this.databaseForm.controls['dateReceivedFromClient'].value;
    if (!dateValue) {
      this.toastr.warning("Please select 'Date Received from Client' first.");
      return;
    }

    const d = new Date(dateValue);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    this.commonService.createUniqueNumber({ date: `${year}-${month}-${day}` }).subscribe({
      next: (res: any) => {
        if (res?.unique_no) {
          this.databaseForm.controls['merakiUniqueNumber'].setValue(res.unique_no);
          this.databaseForm.controls['merakiUniqueNumber'].disable();
        }
      },
      error: (err) => console.error("Error fetching unique number:", err)
    });
  }

  /** ------------------- Dark Mode ------------------- **/
  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }
}
